import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import type {
  AgentInfo,
  CommandInfo,
  FormatterStatus,
  LspStatus,
  Provider,
  ProviderAuthFlow,
  ProviderAuthMethodInfo,
  ProviderUsageInfo,
  SkillInfo,
  UpdateStatus,
} from "../types";
import { Modal, ProviderKeyModal } from "./Modals";
import { ResetCreditsPanel } from "./ResetCreditsPanel";
import { cx } from "../util";
import { getProviderUsage, remainingPercent, resetIn } from "../usage";

type Tab =
  | "sessao"
  | "provedores"
  | "agentes"
  | "comandos"
  | "mcp"
  | "skills"
  | "lsp"
  | "config"
  | "atualizacoes"
  | "ajuda";

export function SettingsModal({
  directory,
  sessionId,
  sessionTitle,
  allowAppRun,
  allowAllPermissions,
  permissionsSaving,
  sessionPrefsLoading,
  onAllowAllPermissionsChange,
  onToggleAllowAppRun,
  onClose,
  onToast,
  onEngineUpdated,
}: {
  directory: string;
  sessionId: string | null;
  sessionTitle?: string | null;
  allowAppRun: boolean;
  allowAllPermissions: boolean;
  permissionsSaving: boolean;
  sessionPrefsLoading: boolean;
  onAllowAllPermissionsChange: (allow: boolean) => void;
  onToggleAllowAppRun: () => void;
  onClose: () => void;
  onToast: (msg: string, kind?: "info" | "error" | "success") => void;
  onEngineUpdated?: () => void;
}) {
  const [tab, setTab] = useState<Tab>("provedores");
  const [providers, setProviders] = useState<Provider[]>([]);
  const [connected, setConnected] = useState<string[]>([]);
  /** Texto de busca na lista de provedores (nome, id, origem, variáveis, modelos). */
  const [providerQuery, setProviderQuery] = useState("");
  const [keyTarget, setKeyTarget] = useState<Provider | null>(null);
  const [keySaving, setKeySaving] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [authMethods, setAuthMethods] = useState<Record<string, ProviderAuthMethodInfo[]>>({});
  /** Fluxo de login por conta (OAuth) em andamento, se houver. */
  const [oauthFlow, setOauthFlow] = useState<{
    providerId: string;
    providerName: string;
    methodIndex: number;
    flow: ProviderAuthFlow;
    code: string;
    busy: boolean;
    error: string | null;
    initiallyConnected: boolean;
  } | null>(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [commands, setCommands] = useState<CommandInfo[]>([]);
  const [mcp, setMcp] = useState<Record<string, unknown>>({});
  const [skills, setSkills] = useState<SkillInfo[]>([]);
  /** Consulta de uso/créditos da conta OpenAI (plano ChatGPT) em andamento. */
  const [usage, setUsage] = useState<{
    providerId: string;
    busy: boolean;
    error: string | null;
    data: ProviderUsageInfo | null;
  } | null>(null);
  const [lsp, setLsp] = useState<LspStatus[]>([]);
  const [formatter, setFormatter] = useState<FormatterStatus[]>([]);
  const [configText, setConfigText] = useState("");
  const [configError, setConfigError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [update, setUpdate] = useState<UpdateStatus | null>(null);
  const [updateBusy, setUpdateBusy] = useState<string | null>(null);

  /** Provedores filtrados pelo texto de busca (nome, id, origem, variáveis, modelos). */
  const filteredProviders = useMemo(() => {
    const q = providerQuery.trim().toLowerCase();
    if (!q) return providers;
    return providers.filter((p) => {
      const haystack = [
        p.name,
        p.id,
        p.source,
        ...(p.env ?? []),
        ...Object.values(p.models ?? {}).map((m) => `${m.id} ${m.name ?? ""}`),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [providers, providerQuery]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const [prov, agentsRes, cmds, mcpRes, skillsRes, lspRes, fmtRes, cfg, upd, authM] = await Promise.allSettled([
          api.providers(directory),
          api.agents(directory),
          api.commands(directory),
          api.mcp(directory),
          api.skills(directory),
          api.lsp(directory),
          api.formatter(directory),
          api.config(directory),
          api.updateStatus(),
          api.providerAuthMethods(directory),
        ]);
        if (prov.status === "fulfilled") {
          setProviders(prov.value.all ?? []);
          setConnected(prov.value.connected ?? []);
        }
        if (agentsRes.status === "fulfilled") setAgents(agentsRes.value);
        if (cmds.status === "fulfilled") setCommands(cmds.value);
        if (mcpRes.status === "fulfilled") setMcp(mcpRes.value);
        if (skillsRes.status === "fulfilled") setSkills(skillsRes.value);
        if (lspRes.status === "fulfilled") setLsp(lspRes.value);
        if (fmtRes.status === "fulfilled") setFormatter(fmtRes.value);
        if (cfg.status === "fulfilled") setConfigText(JSON.stringify(cfg.value, null, 2));
        if (upd.status === "fulfilled") setUpdate(upd.value);
        if (authM.status === "fulfilled") setAuthMethods(authM.value ?? {});
      } finally {
        setLoading(false);
      }
    })();
  }, [directory]);

  async function saveConfig() {
    setConfigError(null);
    try {
      const parsed = JSON.parse(configText) as Record<string, unknown>;
      const saved = await api.updateConfig(directory, parsed);
      setConfigText(JSON.stringify(saved, null, 2));
      onToast("Configuração salva no arquivo do OpenCode. Reinicie o serviço OpenCode para aplicar.", "success");
    } catch (e) {
      setConfigError((e as Error).message);
    }
  }

  /** Consulta o registro do npm agora (não atualiza; só verifica). */
  async function checkUpdate() {
    setUpdateBusy("check");
    try {
      const s = await api.updateCheck();
      setUpdate(s);
      onToast(
        s.updateAvailable
          ? `Nova versão disponível: ${s.latestVersion} (instalada: ${s.engineVersion}).`
          : `Motor atualizado: ${s.engineVersion} é a última versão publicada.`,
        "success",
      );
    } catch (e) {
      onToast((e as Error).message, "error");
    } finally {
      setUpdateBusy(null);
    }
  }

  /** Solicita atualização imediata e acompanha até o motor reiniciar. */
  async function runUpdate() {
    setUpdateBusy("run");
    try {
      await api.updateRun();
      onToast("Atualização solicitada. O motor será reiniciado em instantes…", "info");
      const started = Date.now();
      while (Date.now() - started < 120_000) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const s = await api.updateStatus();
          setUpdate(s);
          if (s.phase === "updated" && s.engineVersion && s.engineVersion === s.latestVersion) {
            onToast(`OpenCode atualizado para ${s.engineVersion}. Provedores recarregados.`, "success");
            onEngineUpdated?.();
            return;
          }
          if (s.phase === "error") {
            onToast(`Falha na atualização: ${s.message ?? "erro desconhecido"}`, "error");
            return;
          }
        } catch {
          /* motor reiniciando; tenta de novo */
        }
      }
      onToast("Atualização em andamento demorou mais que o esperado; verifique novamente em instantes.", "info");
    } catch (e) {
      onToast((e as Error).message, "error");
    } finally {
      setUpdateBusy(null);
    }
  }

  async function setUpdateSettings(patch: { autoUpdate?: boolean; intervalHours?: number }) {
    try {
      const res = await api.updateSettings(patch);
      setUpdate((u) => (u ? { ...u, control: res.control } : u));
      onToast("Preferência de atualização automática salva.", "success");
    } catch (e) {
      onToast((e as Error).message, "error");
    }
  }

  /** Salva a chave do provedor no servidor OpenCode (nunca devolvida ao navegador). */
  async function saveProviderKey(key: string) {
    if (!keyTarget) return;
    setKeySaving(true);
    setKeyError(null);
    try {
      const res = await api.setProviderAuth(keyTarget.id, key);
      setConnected(res.connected ?? []);
      onToast(`Credencial de ${keyTarget.name} salva no servidor.`, "success");
      setKeyTarget(null);
    } catch (e) {
      setKeyError((e as Error).message);
    } finally {
      setKeySaving(false);
    }
  }

  async function removeProviderKey(p: Provider) {
    if (!window.confirm(`Remover a credencial salva de ${p.name}? Os modelos dele voltarão a exigir chave.`)) return;
    try {
      const res = await api.removeProviderAuth(p.id);
      setConnected(res.connected ?? []);
      onToast(`Credencial de ${p.name} removida.`, "success");
    } catch (e) {
      onToast((e as Error).message, "error");
    }
  }

  /** Consulta uso e créditos da conta conectada (plano ChatGPT). */
  async function checkUsage(p: Provider) {
    setUsage({ providerId: p.id, busy: true, error: null, data: null });
    try {
      const data = await getProviderUsage(p.id, true);
      setUsage({ providerId: p.id, busy: false, error: null, data });
    } catch (e) {
      setUsage({ providerId: p.id, busy: false, error: (e as Error).message, data: null });
    }
  }

  /** Inicia o login por conta (OAuth) do provedor no motor OpenCode. */
  async function startOauthLogin(p: Provider, methodIndex: number) {
    try {
      const flow = await api.providerOauthStart(p.id, methodIndex);
      setOauthFlow({
        providerId: p.id,
        providerName: p.name,
        methodIndex,
        flow,
        code: "",
        busy: false,
        error: null,
        initiallyConnected: connected.includes(p.id),
      });
    } catch (e) {
      onToast((e as Error).message, "error");
    }
  }

  /** Conclui o login (com código, quando a página de autorização exibir um). */
  async function completeOauthLogin() {
    if (!oauthFlow) return;
    setOauthFlow({ ...oauthFlow, busy: true, error: null });
    try {
      const res = await api.providerOauthComplete(
        oauthFlow.providerId,
        oauthFlow.methodIndex,
        oauthFlow.code.trim() || undefined,
      );
      setConnected(res.connected ?? []);
      if (res.ok) {
        onToast(`Login de ${oauthFlow.providerName} concluído. Credencial salva no servidor.`, "success");
        setOauthFlow(null);
      } else {
        // Fluxo ainda aguardando autorização no site do provedor (device):
        // mantém o painel aberto; a detecção automática conclui o login.
        setOauthFlow({ ...oauthFlow, busy: false, error: null });
        onToast(res.message ?? "Aguardando a autorização no site do provedor…", "info");
      }
    } catch (e) {
      setOauthFlow({ ...oauthFlow, busy: false, error: (e as Error).message });
    }
  }

  // Verificação automática: nos fluxos por código de dispositivo o usuário
  // autoriza no site do provedor e a credencial aparece sozinha no servidor.
  useEffect(() => {
    if (!oauthFlow || oauthFlow.initiallyConnected) return;
    const started = Date.now();
    const timer = window.setInterval(() => {
      void (async () => {
        if (Date.now() - started > 300_000) {
          window.clearInterval(timer);
          return;
        }
        try {
          const res = await api.providers(directory);
          setConnected(res.connected ?? []);
          if (res.connected.includes(oauthFlow.providerId)) {
            onToast(`Login de ${oauthFlow.providerName} concluído. Credencial salva no servidor.`, "success");
            setOauthFlow(null);
          }
        } catch {
          /* motor ocupado; tenta de novo no próximo ciclo */
        }
      })();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [oauthFlow, directory]);

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: "sessao", label: "Sessão atual" },
    { id: "provedores", label: "Provedores" },
    { id: "agentes", label: "Agentes" },
    { id: "comandos", label: "Comandos" },
    { id: "mcp", label: "MCP" },
    { id: "skills", label: "Skills" },
    { id: "lsp", label: "LSP & Formatadores" },
    { id: "config", label: "Configuração" },
    { id: "atualizacoes", label: "Atualizações" },
    { id: "ajuda", label: "Ajuda & TUI" },
  ];

  return (
    <>
    <Modal title="Configurações do OpenCode" onClose={onClose} wide>
      <div className="panel-tabs" style={{ borderBottom: "1px solid var(--border)", marginBottom: 14 }} role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={cx("panel-tab", tab === t.id && "active")}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? <p>Carregando…</p> : null}

      {tab === "sessao" ? (
        <div>
          {sessionId ? (
            <>
              <p style={{ color: "var(--text-muted)", fontSize: 13 }}>
                Configurações aplicadas somente a esta sessão
                {sessionTitle ? ` (${sessionTitle})` : ""}. Ficam salvas no servidor e sobrevivem a
                reinicializações.
              </p>
              <div className="diff-file" style={{ marginBottom: 10 }}>
                <div style={{ padding: "12px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={allowAllPermissions}
                      disabled={permissionsSaving || sessionPrefsLoading}
                      onChange={(e) => onAllowAllPermissionsChange(e.target.checked)}
                      aria-describedby="settings-allow-all-description"
                    />
                    <strong>Permitir tudo nesta sessão</strong>
                  </label>
                  <p id="settings-allow-all-description" style={{ fontSize: 13, color: "var(--text-muted)", margin: "8px 0 0" }}>
                    Comandos e alterações em arquivos ficam sem confirmação, incluindo iniciar a aplicação.
                    A opção fica salva somente nesta sessão.
                  </p>
                  {permissionsSaving || sessionPrefsLoading ? (
                    <p role="status" style={{ fontSize: 12, color: "var(--text-muted)", margin: "8px 0 0" }}>
                      {sessionPrefsLoading ? "Carregando permissões da sessão…" : "Salvando permissões…"}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="diff-file" style={{ marginBottom: 10 }}>
                <div className="diff-head">
                  <strong>Rodar aplicação (por sessão)</strong>
                  <span className={cx("chip", allowAllPermissions || allowAppRun ? "ok" : "err")}>
                    {allowAllPermissions ? "incluído em permitir tudo" : allowAppRun ? "ativado" : "desativado"}
                  </span>
                </div>
                <div style={{ padding: "8px 12px" }}>
                  <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "0 0 8px" }}>
                    Comandos comuns — instalar dependências, compilar, rodar testes, git —{" "}
                    <strong>executam sempre</strong>. Quando desativado (padrão), o OpenCode apenas
                    não inicia a aplicação nesta sessão: comandos como <code>npm run dev</code>,{" "}
                    <code>uvicorn</code>, <code>docker compose up</code> são bloqueados com aviso.
                  </p>
                  <button
                    className={cx("btn", allowAppRun ? "danger" : "primary")}
                    onClick={onToggleAllowAppRun}
                    disabled={allowAllPermissions || permissionsSaving || sessionPrefsLoading}
                  >
                    {allowAllPermissions ? "Aplicação pode rodar com permitir tudo" : allowAppRun ? "Desativar rodar aplicação" : "Ativar rodar aplicação"}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <p style={{ color: "var(--text-muted)", fontSize: 13 }}>
              Abra ou crie uma sessão para configurar as opções por sessão.
            </p>
          )}
        </div>
      ) : null}

      {tab === "provedores" && !loading ? (
        <div>
          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>
            As credenciais dos provedores ficam somente no servidor (variáveis de ambiente ou{" "}
            <code>opencode.json</code> montado no contêiner). A interface mostra apenas estado de conexão.
          </p>
          {providers.length > 0 ? (
            <div style={{ display: "flex", gap: 6, alignItems: "center", margin: "8px 0 12px" }}>
              <input
                className="input"
                style={{ flex: 1, minWidth: 200 }}
                placeholder="Buscar provedor por nome, id, origem, variável ou modelo…"
                value={providerQuery}
                onChange={(e) => setProviderQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setProviderQuery("");
                }}
              />
              {providerQuery ? (
                <button className="btn sm" onClick={() => setProviderQuery("")}>
                  Limpar
                </button>
              ) : null}
              <span className="chip" style={{ whiteSpace: "nowrap" }}>
                {filteredProviders.length} de {providers.length}
              </span>
            </div>
          ) : null}
          {providers.length === 0 ? (
            <p>Nenhum provedor disponível. Configure uma chave de API no servidor e reinicie o serviço OpenCode.</p>
          ) : filteredProviders.length === 0 ? (
            <p>
              Nenhum provedor corresponde a <strong>{providerQuery.trim()}</strong>.
            </p>
          ) : (
            filteredProviders.map((p) => {
              const conn = connected.includes(p.id);
              return (
                <div key={p.id} className="diff-file" style={{ marginBottom: 10 }}>
                  <div className="diff-head" style={{ gap: 10 }}>
                    <strong>{p.name}</strong>
                    <span className="chip">{p.source}</span>
                    <span className={cx("chip", conn ? "ok" : "err")}>
                      {conn ? "conectado" : "sem autenticação"}
                    </span>
                    <span className="chip">{Object.keys(p.models ?? {}).length} modelo(s)</span>
                  </div>
                  <div style={{ padding: "8px 12px" }}>
                    {p.env?.length ? (
                      <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 0" }}>
                        Variáveis esperadas: {p.env.map((e) => (
                          <code key={e} style={{ marginRight: 6 }}>
                            {e}
                          </code>
                        ))}
                      </p>
                    ) : null}
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {Object.values(p.models ?? {})
                        .slice(0, 12)
                        .map((m) => (
                          <span key={m.id} className="chip">
                            {m.name ?? m.id}
                          </span>
                        ))}
                    </div>
                    {(authMethods[p.id] ?? []).some((m) => m.type === "oauth") ? (
                      <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {(authMethods[p.id] ?? []).map((m, i) =>
                          m.type === "oauth" ? (
                            <button key={i} className="btn sm primary" disabled={p.id === "openai" && resetBusy} onClick={() => void startOauthLogin(p, i)}>
                              Entrar com conta — {m.label}
                            </button>
                          ) : null,
                        )}
                      </div>
                    ) : null}
                    {oauthFlow && oauthFlow.providerId === p.id ? (
                      <div
                        className="diff-file"
                        style={{ marginTop: 10, borderColor: "var(--accent, #6c8)" }}
                      >
                        <div className="diff-head">
                          <strong>Login por conta — {oauthFlow.flow.method === "code" ? "por código" : "no navegador"}</strong>
                          <span className="chip warn">aguardando autorização</span>
                        </div>
                        <div style={{ padding: "8px 12px" }}>
                          <pre
                            style={{
                              margin: "4px 0 8px",
                              padding: "8px 10px",
                              background: "var(--bg-alt, rgba(0,0,0,.15))",
                              borderRadius: 6,
                              whiteSpace: "pre-wrap",
                              fontFamily: "var(--font-mono)",
                              fontSize: 12.5,
                            }}
                          >
                            {oauthFlow.flow.instructions}
                          </pre>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                            <a
                              className="btn sm primary"
                              href={oauthFlow.flow.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Abrir página de login
                            </a>
                            <button
                              className="btn sm"
                              onClick={() => void navigator.clipboard.writeText(oauthFlow.flow.url)}
                            >
                              Copiar link
                            </button>
                          </div>
                          {oauthFlow.flow.method === "code" ? (
                            <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "8px 0 4px" }}>
                              Após autorizar, o login é detectado automaticamente (pode levar alguns
                              segundos). Se a página final pedir um código, cole abaixo:
                            </p>
                          ) : (
                            <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "8px 0 4px" }}>
                              Ao autorizar, o site do provedor redireciona o navegador para{" "}
                              <code>http://localhost:1455/auth/callback…</code> e essa página dá erro —
                              é esperado quando o aplicativo é acessado por um link remoto.{" "}
                              <strong>
                                Copie a URL completa da barra de endereço (ela contém{" "}
                                <code>code=…</code>) e cole abaixo
                              </strong>
                              : o login é concluído daqui, no servidor.
                            </p>
                          )}
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                            <input
                              className="input"
                              style={{ flex: 1, minWidth: 220 }}
                              placeholder={
                                oauthFlow.flow.method === "code"
                                  ? "Código de autorização"
                                  : "http://localhost:1455/auth/callback?code=… (URL inteira) ou código"
                              }
                              value={oauthFlow.code}
                              onChange={(e) => setOauthFlow({ ...oauthFlow, code: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") void completeOauthLogin();
                              }}
                            />
                            <button
                              className="btn sm primary"
                              disabled={oauthFlow.busy}
                              onClick={() => void completeOauthLogin()}
                            >
                              {oauthFlow.busy ? "Concluindo…" : "Concluir login"}
                            </button>
                            <button className="btn sm" onClick={() => setOauthFlow(null)}>
                              Cancelar
                            </button>
                          </div>
                          {oauthFlow.error ? (
                            <p style={{ color: "var(--danger, #c33)", fontSize: 12.5, margin: "6px 0 0" }}>
                              {oauthFlow.error}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                    {(p.env?.length ?? 0) > 0 ? (
                      <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                        <button
                          className="btn sm"
                          disabled={p.id === "openai" && resetBusy}
                          onClick={() => {
                            setKeyError(null);
                            setKeyTarget(p);
                          }}
                        >
                          {conn ? "Trocar chave" : "Definir chave"}
                        </button>
                        {conn ? (
                          <button className="btn sm danger" disabled={p.id === "openai" && resetBusy} onClick={() => void removeProviderKey(p)}>
                            Remover chave
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                    {p.id === "openai" && conn ? (
                      <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <button
                          className="btn sm"
                          disabled={resetBusy || (usage?.busy && usage.providerId === p.id)}
                          onClick={() => void checkUsage(p)}
                        >
                          {usage?.busy && usage.providerId === p.id
                            ? "Consultando…"
                            : "Verificar uso e créditos"}
                        </button>
                      </div>
                    ) : null}
                    {usage && usage.providerId === p.id && (usage.data || usage.error) ? (
                      <div
                        className="diff-file"
                        style={{ marginTop: 10, borderColor: "var(--accent, #6c8)" }}
                      >
                        <div className="diff-head">
                          <strong>Uso e créditos — {usage.data?.planLabel ?? "conta OpenAI"}</strong>
                          <span className="chip">janelas do plano + créditos</span>
                        </div>
                        <div style={{ padding: "8px 12px", fontSize: 12.5 }}>
                          {usage.error ? (
                            <p style={{ color: "var(--danger, #c33)", margin: "4px 0" }}>{usage.error}</p>
                          ) : null}
                          {usage.data ? (
                            <>
                              <ul style={{ margin: "4px 0", paddingLeft: 18 }}>
                                {usage.data.windows.map((w) => {
                                  const reset = resetIn(w);
                                  const remaining = remainingPercent(w);
                                  return (
                                    <li key={w.id} style={{ margin: "2px 0" }}>
                                      {w.label}:{" "}
                                      <strong
                                        style={{
                                          color:
                                            w.limitReached || remaining <= 20
                                              ? "var(--danger, #c33)"
                                              : remaining <= 50
                                                ? "var(--warn, #b80)"
                                                : undefined,
                                        }}
                                      >
                                        {remaining}% disponíveis
                                      </strong>
                                      {reset ? <> · renovam em {reset}</> : null}
                                      {w.limitReached ? (
                                        <span className="chip err" style={{ marginLeft: 6 }}>
                                          limite atingido
                                        </span>
                                      ) : null}
                                    </li>
                                  );
                                })}
                              </ul>
                              <p style={{ margin: "4px 0" }}>
                                Créditos extras:{" "}
                                <strong>
                                  {usage.data.credits?.unlimited
                                    ? "ilimitados"
                                    : usage.data.credits?.balance !== undefined
                                      ? `US$ ${usage.data.credits.balance}`
                                      : usage.data.credits?.hasCredits
                                        ? "disponíveis"
                                        : "nenhum"}
                                </strong>
                                {usage.data.resetCreditsAvailable !== undefined ? (
                                  <>
                                    {" "}
                                    · resets de limite para resgate:{" "}
                                    <strong>{usage.data.resetCreditsAvailable}</strong>
                                  </>
                                ) : null}
                              </p>
                              {usage.data.spendControlReached ? (
                                <p style={{ color: "var(--danger, #c33)", margin: "4px 0" }}>
                                  Limite de gastos da conta atingido.
                                </p>
                              ) : null}
                              <p style={{ color: "var(--text-muted)", margin: "4px 0" }}>
                                Consultado agora junto à OpenAI · os valores seguem a medição oficial do
                                plano.
                              </p>
                            </>
                          ) : null}
                          {p.id === "openai" && conn ? (
                            <ResetCreditsPanel
                              providerID={p.id}
                              onBusyChange={setResetBusy}
                              onUsageChange={(data) => setUsage({
                                providerId: p.id,
                                busy: false,
                                data,
                                error: data ? null : "Os limites aguardam uma nova consulta à OpenAI.",
                              })}
                            />
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : null}

      {tab === "agentes" && !loading ? (
        <div>
          {agents.map((a) => (
            <div key={a.name} className="tree-item" style={{ cursor: "default", alignItems: "flex-start" }}>
              <span className="chip">{a.mode ?? "agent"}</span>
              <span style={{ flex: 1, fontFamily: "var(--font-sans)" }}>
                <strong>{a.name}</strong>
                <div style={{ color: "var(--text-muted)", fontSize: 12.5 }}>{a.description}</div>
              </span>
              {a.model ? (
                <span className="chip">
                  {a.model.providerID}/{a.model.modelID}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {tab === "comandos" && !loading ? (
        <div>
          {commands.length === 0 ? <p>Nenhum comando personalizado registrado.</p> : null}
          {commands.map((c) => (
            <div key={c.name} className="tree-item" style={{ cursor: "default", alignItems: "flex-start" }}>
              <span className="tool-name">/{c.name}</span>
              <span style={{ flex: 1, fontFamily: "var(--font-sans)", color: "var(--text-muted)" }}>
                {c.description ?? c.template}
              </span>
              <span className="chip">{c.source ?? "command"}</span>
            </div>
          ))}
        </div>
      ) : null}

      {tab === "mcp" && !loading ? (
        <div>
          {Object.keys(mcp).length === 0 ? <p>Nenhum servidor MCP configurado.</p> : null}
          {Object.entries(mcp).map(([name, status]) => {
            const st = status as { type?: string };
            return (
              <div key={name} className="tree-item" style={{ cursor: "default" }}>
                <span className={cx("chip", st.type === "connected" ? "ok" : st.type === "failed" ? "err" : "warn")}>
                  {st.type ?? "desconhecido"}
                </span>
                <span style={{ flex: 1, fontFamily: "var(--font-mono)" }}>{name}</span>
                <button className="btn sm" onClick={() => void api.mcpConnect(directory, name).then(() => onToast(`Conectando ${name}…`)).catch((e: Error) => onToast(e.message, "error"))}>
                  Conectar
                </button>
                <button className="btn sm" onClick={() => void api.mcpDisconnect(directory, name).then(() => onToast(`Desconectado ${name}`)).catch((e: Error) => onToast(e.message, "error"))}>
                  Desconectar
                </button>
              </div>
            );
          })}
          <p style={{ color: "var(--text-muted)", fontSize: 12.5, marginTop: 12 }}>
            Servidores MCP são adicionados no <code>opencode.json</code> do servidor (seção <code>mcp</code>) ou pelo
            painel de configuração abaixo.
          </p>
        </div>
      ) : null}

      {tab === "skills" && !loading ? (
        <div>
          {skills.length === 0 ? <p>Nenhuma skill registrada.</p> : null}
          {skills.map((s) => (
            <div key={s.name} className="diff-file" style={{ marginBottom: 8 }}>
              <div className="diff-head">
                <strong>{s.name}</strong>
                <span style={{ flex: 1, fontFamily: "var(--font-sans)", color: "var(--text-muted)" }}>
                  {s.description}
                </span>
                {s.location ? <span className="chip">{s.location}</span> : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {tab === "lsp" && !loading ? (
        <div>
          <div className="side-label">Servidores LSP</div>
          {lsp.length === 0 ? <p>Nenhum LSP ativo.</p> : null}
          {lsp.map((l) => (
            <div key={l.id} className="tree-item" style={{ cursor: "default" }}>
              <span className={cx("chip", l.status === "connected" ? "ok" : "err")}>{l.status}</span>
              <span style={{ flex: 1 }}>{l.name}</span>
              <span className="chip">{l.root}</span>
            </div>
          ))}
          <div className="side-label">Formatadores</div>
          {formatter.map((f) => (
            <div key={f.name} className="tree-item" style={{ cursor: "default" }}>
              <span className={cx("chip", f.enabled ? "ok" : "")}>{f.enabled ? "ativo" : "inativo"}</span>
              <span style={{ flex: 1 }}>{f.name}</span>
              <span className="chip">{f.extensions.join(", ")}</span>
            </div>
          ))}
        </div>
      ) : null}

      {tab === "config" && !loading ? (
        <div>
          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>
            Edição direta do <code>opencode.json</code> do servidor via API oficial. Não cole chaves de API aqui —
            prefira variáveis de ambiente do contêiner.
          </p>
          <textarea
            className="input"
            value={configText}
            onChange={(e) => setConfigText(e.target.value)}
            spellCheck={false}
            aria-label="Configuração JSON do OpenCode"
          />
          {configError ? <div className="error-banner">{configError}</div> : null}
          <button className="btn primary" onClick={() => void saveConfig()}>
            Salvar configuração
          </button>
        </div>
      ) : null}

      {tab === "atualizacoes" && !loading ? (
        <div>
          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>
            Quando sai uma nova versão do OpenCode no npm, o motor é atualizado automaticamente e reiniciado —
            o catálogo de provedores e modelos é recarregado no novo boot. O intervalo padrão de verificação é de{" "}
            {update?.control.intervalHours ?? 6}h.
          </p>

          <div className="diff-file" style={{ marginBottom: 10 }}>
            <div className="diff-head">
              <strong>Motor OpenCode</strong>
              {update?.updateAvailable ? (
                <span className="chip err">atualização disponível</span>
              ) : (
                <span className="chip ok">atualizado</span>
              )}
            </div>
            <div style={{ padding: "8px 12px", fontSize: 13 }}>
              <div>
                Versão instalada: <strong>{update?.engineVersion ?? "desconhecida"}</strong>
                {" · "}
                Última versão publicada: <strong>{update?.latestVersion ?? "—"}</strong>
              </div>
              <div style={{ color: "var(--text-muted)", marginTop: 4 }}>
                {update?.checkedAt ? `Última verificação: ${new Date(update.checkedAt).toLocaleString()}` : "Ainda não verificado."}
                {update?.updatedAt ? ` · Última atualização: ${new Date(update.updatedAt).toLocaleString()}` : ""}
              </div>
              {update?.message ? (
                <div style={{ color: "var(--text-muted)", marginTop: 4 }}>{update.message}</div>
              ) : null}
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                <button className="btn" disabled={updateBusy !== null} onClick={() => void checkUpdate()}>
                  {updateBusy === "check" ? "Verificando…" : "Verificar agora"}
                </button>
                <button
                  className="btn primary"
                  disabled={updateBusy !== null}
                  onClick={() => {
                    if (window.confirm("Atualizar o motor OpenCode agora? O motor reinicia e as sessões em andamento podem ser interrompidas.")) {
                      void runUpdate();
                    }
                  }}
                >
                  {updateBusy === "run" ? "Atualizando…" : "Atualizar agora"}
                </button>
              </div>
            </div>
          </div>

          <div className="diff-file" style={{ marginBottom: 10 }}>
            <div className="diff-head">
              <strong>Atualização automática</strong>
              <span className={cx("chip", update?.control.autoUpdate ? "ok" : "err")}>
                {update?.control.autoUpdate ? "ativada" : "desativada"}
              </span>
            </div>
            <div style={{ padding: "8px 12px", fontSize: 13 }}>
              <p style={{ color: "var(--text-muted)", margin: "0 0 8px" }}>
                Com a atualização automática ativada (padrão), o supervisor do motor verifica o registro do npm
                periodicamente e aplica novas versões do <code>opencode-ai</code> sozinho, reiniciando o motor.
                Provedores e modelos são recarregados após o reinício.
              </p>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button
                  className={cx("btn", update?.control.autoUpdate ? "danger" : "primary")}
                  onClick={() => void setUpdateSettings({ autoUpdate: !update?.control.autoUpdate })}
                >
                  {update?.control.autoUpdate ? "Desativar automático" : "Ativar automático"}
                </button>
                <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  Verificar a cada
                  <select
                    className="input"
                    style={{ width: "auto" }}
                    value={update?.control.intervalHours ?? 6}
                    onChange={(e) => void setUpdateSettings({ intervalHours: Number(e.target.value) })}
                  >
                    <option value={1}>1 hora</option>
                    <option value={6}>6 horas</option>
                    <option value={12}>12 horas</option>
                    <option value={24}>24 horas</option>
                  </select>
                </label>
              </div>
            </div>
          </div>

          {update && update.history.length > 0 ? (
            <div className="diff-file">
              <div className="diff-head">
                <strong>Histórico de atualizações</strong>
              </div>
              <div style={{ padding: "8px 12px", fontSize: 12.5 }}>
                {update.history.map((h, i) => (
                  <div key={i} style={{ padding: "3px 0", color: "var(--text-muted)" }}>
                    {new Date(h.at).toLocaleString()} — {h.from} → {h.to} {h.ok ? "✓" : "✗"}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === "ajuda" && !loading ? (
        <div style={{ fontSize: 13.5 }}>
          <h3>Recursos nativos (TUI) do OpenCode</h3>
          <p>
            Alguns recursos oficiais do OpenCode existem apenas na interface de terminal (TUI). Esta aplicação mostra
            onde acessá-los e mantém tudo o que a API HTTP oferece disponível na web:
          </p>
          <ul>
            <li>
              <strong>Terminal interativo completo (PTY/attach):</strong> abra o terminal do contêiner com{" "}
              <code>docker exec -it &lt;contêiner-opencode&gt; opencode</code> (ou o console do Easypanel).
            </li>
            <li>
              <strong>Tema, modelos e sessões via TUI:</strong> os atalhos da TUI continuam disponíveis no terminal;
              na web use os seletores desta interface.
            </li>
            <li>
              <strong>OAuth interativo de provedores:</strong> execute <code>opencode auth login</code> no terminal do
              contêiner; o estado de conexão aparece na aba Provedores.
            </li>
            <li>
              <strong>Plugin/tema/instalação da TUI:</strong> gerenciados pelos comandos oficiais no terminal.
            </li>
          </ul>
          <h3>Backup</h3>
          <p>
            Projetos, sessões e configurações vivem em volumes persistentes (<code>opencode-data</code>,{" "}
            <code>opencode-config</code>, <code>workspace</code>, <code>app-data</code>). O procedimento completo de
            backup/restauração está no arquivo <code>docs/BACKUP.md</code> do repositório.
          </p>
          <h3>Caminho do servidor OpenCode</h3>
          <p>
            O serviço OpenCode fica somente na rede interna do Compose. Para diagnóstico use o terminal do contêiner —
            ele nunca é exposto publicamente.
          </p>
        </div>
      ) : null}
    </Modal>
      {keyTarget ? (
        <ProviderKeyModal
          provider={keyTarget}
          saving={keySaving}
          error={keyError}
          onSave={(k) => void saveProviderKey(k)}
          onClose={() => setKeyTarget(null)}
        />
      ) : null}
    </>
  );
}
